
import { EnhancedModule } from "@/data/modules.ts";

// Calculate savings metrics based on active modules
export const calculateSavingsMetrics = (modules: EnhancedModule[]) => {
  const totalActiveSubmodules = modules.reduce(
    (total, module) => total + module.subModules.filter(sm => sm.active).length, 
    0
  );
  
  // Default values when no modules are active
  const defaultTimeReduction = 65;
  const defaultCostReduction = 50;
  const defaultErrorReduction = 75;
  const defaultTimeSaved = 14; // 40 hours * 0.35 efficiency
  const defaultAnnualSavings = 60000;
  const defaultRoi = 200;
  const defaultAnnualCost = 30000;
  
  // Use actual values if modules are active, otherwise use defaults
  const timeReduction = totalActiveSubmodules > 0 
    ? 65 + (totalActiveSubmodules * 5) 
    : defaultTimeReduction;
    
  const costReduction = totalActiveSubmodules > 0 
    ? 50 + (totalActiveSubmodules * 3) 
    : defaultCostReduction;
    
  const errorReduction = totalActiveSubmodules > 0 
    ? 75 + (totalActiveSubmodules * 2) 
    : defaultErrorReduction;
  
  const originalTime = 40; // hours per week
  const timeHoursPerWeek = Math.round(originalTime * (100 - timeReduction) / 100);
  const timeSaved = totalActiveSubmodules > 0 
    ? originalTime - timeHoursPerWeek 
    : defaultTimeSaved;
  
  // Calculate ROI
  const annualCost = totalActiveSubmodules > 0 
    ? (1000 * 12 + totalActiveSubmodules * 300 * 12) 
    : defaultAnnualCost;
    
  const annualSavings = totalActiveSubmodules > 0 
    ? Math.round(120000 * costReduction / 100) 
    : defaultAnnualSavings;
    
  const roi = totalActiveSubmodules > 0 
    ? Math.round((annualSavings / annualCost) * 100) 
    : defaultRoi;

  return {
    timeReduction,
    costReduction,
    errorReduction,
    timeSaved,
    annualSavings,
    annualCost,
    roi,
    totalActiveSubmodules
  };
};

// Calculate pricing information based on active modules
export const calculatePricing = (modules: EnhancedModule[]) => {
  const totalActiveSubmodules = modules.reduce(
    (total, module) => total + module.subModules.filter(sm => sm.active).length, 
    0
  );
  
  const defaultFixedPrice = 1000;
  const defaultPerUserPrice = 25;
  
  // Calculate fixed price with volume discount
  const basePrice = 300; // Base price per month in euros
  const pricePerSubmodule = 175; // Price per active submodule
  
  let fixedPrice = totalActiveSubmodules > 0 
    ? (basePrice + (totalActiveSubmodules * pricePerSubmodule)) 
    : defaultFixedPrice;
  
  // Apply discounts based on number of active submodules
  if (totalActiveSubmodules >= 3) fixedPrice = fixedPrice * 0.9; // 10% discount for 3+ modules
  if (totalActiveSubmodules >= 5) fixedPrice = fixedPrice * 0.9; // Additional 10% discount for 5+ modules
  if (totalActiveSubmodules >= 7) fixedPrice = fixedPrice * 0.9; // Additional 10% discount for 7+ modules
  
  // Cap at 4000 euros
  fixedPrice = Math.min(fixedPrice, 4000);
  
  // Calculate per-user pricing
  const baseUserPrice = 25; // Base price per user
  const userPriceDiscount = totalActiveSubmodules > 0
    ? 0.9 ** Math.min(totalActiveSubmodules, 5) 
    : 1; // Discount per active module (max 5 modules for discount)
    
  const perUserPrice = totalActiveSubmodules > 0
    ? Math.round(baseUserPrice * (1 + (totalActiveSubmodules * 0.2)) * userPriceDiscount) 
    : defaultPerUserPrice;
    
  return {
    fixedPrice: Math.round(fixedPrice),
    perUserPrice
  };
};



export const processModuleData = (modules: EnhancedModule[]) => {
  return modules
    .filter(m => m.subModules.some(sm => sm.active))
    .map(module => {
      const activeSubmodules = module.subModules.filter(sm => sm.active);
      const activeCount = activeSubmodules.length;
      const efficiency = 60 + (activeCount * 5);
      const originalHours = 6 * activeCount;
      const optimizedHours = Math.ceil(originalHours * (100-efficiency)/100);
      
      // Calculate module pricing details
      const totalModulePrice = activeSubmodules.reduce((sum, sm) => sum + (sm.price || 0), 0);
      const totalSetupFee = activeSubmodules.reduce((sum, sm) => sum + (sm.setupFee || 0), 0);
      const averageImplementation = activeSubmodules.reduce((sum, sm) => sum + (sm.implementation || 0), 0) / 
                                  (activeSubmodules.length || 1);
      // Get all features from active submodules
      const allFeatures = activeSubmodules.flatMap(sm => sm.features || []);
      
      // Determine highest support level
      const supportLevels = {
        'basic': 1,
        'standard': 2,
        'premium': 3
      };
      
      const highestSupportLevel = activeSubmodules.reduce((highest, sm) => {
        const currentLevel = sm.supportLevel || 'basic';
        return supportLevels[currentLevel] > supportLevels[highest] ? currentLevel : highest;
      }, 'basic');
      
      return { 
        name: module.name, 
        originalHours,
        optimizedHours,
        hoursSaved: originalHours - optimizedHours,
        efficiency,
        errorReduction: 70 + activeCount * 3,
        price: totalModulePrice,
        setupFee: totalSetupFee,
        implementation: Math.ceil(averageImplementation),
        features: allFeatures,
        supportLevel: highestSupportLevel
      };
    });
};

// Calculate additional service details
export const calculateServiceDetails = (moduleData: ReturnType<typeof processModuleData>) => {
  // Calculate total setup fee and implementation time from active modules
  const totalSetupFee = moduleData.reduce((sum, mod) => sum + (mod.setupFee || 0), 0);
  const totalImplementationDays = moduleData.reduce((sum, mod) => sum + (mod.implementation || 0), 0);
  
  // Get all features across all active modules
  const allFeatures = moduleData.flatMap(mod => mod.features || []);
  const uniqueFeatures = [...new Set(allFeatures)];
  
  // Determine overall support level
  const supportLevels = ['basic', 'standard', 'premium'];
  const highestSupportLevel = moduleData.length > 0 
    ? moduleData.reduce((highest, mod) => {
        const currentLevel = mod.supportLevel || 'basic';
        const currentIndex = supportLevels.indexOf(currentLevel);
        const highestIndex = supportLevels.indexOf(highest);
        return currentIndex > highestIndex ? currentLevel : highest;
      }, 'basic')
    : 'basic';
    
  return {
    totalSetupFee,
    totalImplementationDays,
    uniqueFeatures,
    highestSupportLevel
  };
};

// Calculate time savings for a single submodule
export const calculateTimeSavingsForSubmodule = (submodule: any) => {
  const originalHours = submodule.originalHours || 0;
  const efficiency = submodule.efficiency || 0;
  const optimizedHours = Math.ceil(originalHours * (100 - efficiency) / 100);
  return originalHours - optimizedHours;
};

// Calculate cost savings for a single submodule
export const calculateCostSavingsForSubmodule = (submodule: any) => {
  const originalHours = submodule.originalHours || 0;
  const efficiency = submodule.efficiency || 0;
  // Assume an hourly cost of €150
  const hourlyCost = 150;
  // Calculate annual savings (52 weeks)
  return Math.round(originalHours * (efficiency / 100) * hourlyCost * 52);
};
